import { useId, useState, type CSSProperties, type KeyboardEvent, type ReactNode } from 'react';
import type { UseQueryResult } from '@tanstack/react-query';
import { errorMessage } from '../lib/errors';

export function PageHeader({ title, intro, actions, eyebrow }: { title: string; intro?: ReactNode; actions?: ReactNode; eyebrow?: string }) {
  return (
    <header className="page-head">
      <div>
        {eyebrow && <p className="eyebrow">{eyebrow}</p>}
        <h1>{title}</h1>
        {intro && <p>{intro}</p>}
      </div>
      {actions && <div className="toolbar">{actions}</div>}
    </header>
  );
}

export function Card({ title, intro, actions, children, className = '', as: Tag = 'section' }: {
  title?: ReactNode; intro?: ReactNode; actions?: ReactNode; children: ReactNode; className?: string; as?: 'section' | 'article' | 'div';
}) {
  return (
    <Tag className={`card ${className}`}>
      {(title || actions) && (
        <div className="card-head">
          <div>
            {title && <h2>{title}</h2>}
            {intro && <p>{intro}</p>}
          </div>
          {actions && <div className="toolbar">{actions}</div>}
        </div>
      )}
      {children}
    </Tag>
  );
}

export function Segmented<T extends string | number>({ options, value, onChange, label }: {
  options: { value: T; label: string }[]; value: T; onChange: (v: T) => void; label: string;
}) {
  return (
    <div className="seg" role="group" aria-label={label}>
      {options.map((o) => (
        <button key={String(o.value)} type="button" aria-pressed={o.value === value} onClick={() => onChange(o.value)}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function Skeleton({ height = 16, width = '100%', style }: { height?: number | string; width?: number | string; style?: CSSProperties }) {
  return <div className="skeleton" style={{ height, width, ...style }} aria-hidden="true" />;
}

export function CardSkeleton({ height = 260, lines = 0 }: { height?: number; lines?: number }) {
  return (
    <div className="card" aria-busy="true" aria-label="Loading">
      <Skeleton height={18} width="40%" />
      <div style={{ height: 14 }} />
      <Skeleton height={height} />
      {Array.from({ length: lines }, (_, i) => (
        <Skeleton key={i} height={14} width={`${80 - i * 10}%`} style={{ marginTop: 10 }} />
      ))}
    </div>
  );
}

export function EmptyState({ title, children, action }: { title: string; children?: ReactNode; action?: ReactNode }) {
  return (
    <div className="empty">
      <h3>{title}</h3>
      {children && <p>{children}</p>}
      {action}
    </div>
  );
}


export function ErrorState({ error, onRetry, title = 'We couldn’t load this' }: { error: unknown; onRetry?: () => void; title?: string }) {
  return (
    <div className="error-box" role="alert">
      <h3>{title}</h3>
      <p>{errorMessage(error)}</p>
      {onRetry && (
        <button type="button" className="btn btn-sm" onClick={onRetry}>
          Try again
        </button>
      )}
    </div>
  );
}

/** Render loading / error / data states for a query in one place. */
export function QueryView<T>({ query, loading, children, isEmpty, empty }: {
  query: UseQueryResult<T>;
  loading?: ReactNode;
  children: (data: T) => ReactNode;
  isEmpty?: (data: T) => boolean;
  empty?: ReactNode;
}) {
  if (query.isPending) return <>{loading ?? <CardSkeleton />}</>;
  if (query.isError) {
    // A new trial has no forecast until its first sales data is processed
    const err = query.error as { status?: number; message?: string };
    if (err?.status === 404 && /no (forecast|sales history)/i.test(err.message ?? '')) {
      return (
        <EmptyState title="No forecast yet" action={<a className="btn btn-primary btn-sm" href="/upload">Add your sales data</a>}>
          Connect Square or upload your sales history. Your first forecast appears a few minutes after it’s processed.
        </EmptyState>
      );
    }
    return <ErrorState error={query.error} onRetry={() => query.refetch()} />;
  }
  if (isEmpty?.(query.data) && empty) return <>{empty}</>;
  return <>{children(query.data)}</>;
}

export function Badge({ tone = 'neutral', children }: { tone?: 'neutral' | 'ok' | 'warn' | 'crit' | 'accent'; children: ReactNode }) {
  return <span className={`badge ${tone === 'neutral' ? '' : `badge-${tone}`}`}>{children}</span>;
}

export function Kpi({ label, value, sub }: { label: string; value: ReactNode; sub?: ReactNode }) {
  return (
    <div className="kpi">
      <p className="kpi-label">{label}</p>
      <div className="kpi-value">{value}</div>
      {sub && <div className="kpi-sub">{sub}</div>}
    </div>
  );
}

export function Delta({ pct, digits = 0, suffix = '' }: { pct: number | null; digits?: number; suffix?: string }) {
  if (pct == null) return <span className="muted">—</span>;
  const r = Math.round(pct * 10 ** digits) / 10 ** digits;
  const cls = r > 0 ? 'delta-up' : r < 0 ? 'delta-down' : 'muted';
  const arrow = r > 0 ? '▲' : r < 0 ? '▼' : '';
  return (
    <span className={cls}>
      {arrow && <span aria-hidden="true">{arrow} </span>}
      {r > 0 ? '+' : r < 0 ? '−' : ''}
      {Math.abs(r).toFixed(digits)}%{suffix}
    </span>
  );
}

export function Switch({ checked, onChange, label, description, disabled }: {
  checked: boolean; onChange: (v: boolean) => void; label: string; description?: ReactNode; disabled?: boolean;
}) {
  const id = useId();
  return (
    <div className="switch">
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-labelledby={`${id}-l`}
        aria-describedby={description ? `${id}-d` : undefined}
        disabled={disabled}
        onClick={() => onChange(!checked)}
      />
      <div>
        <div id={`${id}-l`} style={{ fontWeight: 600 }}>{label}</div>
        {description && <div id={`${id}-d`} className="muted small" style={{ marginTop: 2 }}>{description}</div>}
      </div>
    </div>
  );
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function EmailChips({ value, onChange, id, disabled }: { value: string[]; onChange: (v: string[]) => void; id?: string; disabled?: boolean }) {
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);
  const commit = () => {
    const e = draft.trim().replace(/,$/, '');
    if (!e) return;
    if (!EMAIL_RE.test(e)) {
      setError('That doesn’t look like an email address.');
      return;
    }
    if (!value.includes(e)) onChange([...value, e]);
    setDraft('');
    setError(null);
  };
  const onKey = (ev: KeyboardEvent<HTMLInputElement>) => {
    if (ev.key === 'Enter' || ev.key === ',' || ev.key === ' ') {
      ev.preventDefault();
      commit();
    } else if (ev.key === 'Backspace' && !draft && value.length) {
      onChange(value.slice(0, -1));
    }
  };
  return (
    <div>
      <div className="chips">
        {value.map((e) => (
          <span className="chip" key={e}>
            {e}
            {!disabled && (
              <button type="button" aria-label={`Remove ${e}`} onClick={() => onChange(value.filter((x) => x !== e))}>
                ×
              </button>
            )}
          </span>
        ))}
        {!disabled && (
          <input
            id={id}
            type="email"
            value={draft}
            placeholder={value.length ? 'Add another email' : 'name@business.com.au'}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={onKey}
            onBlur={commit}
            aria-invalid={error ? 'true' : undefined}
          />
        )}
      </div>
      {error && <p className="form-error" style={{ marginTop: 6 }}>{error}</p>}
    </div>
  );
}
